/* ============================================================================
 * ตัวเลือกของเมนู "จัดการหน้าแรก" (/ad/home/*)
 *
 * เนื้อหาจริงทั้งหมดอยู่ในฐานข้อมูล — หน้าแอดมินอ่าน/เขียนผ่าน API รายกลุ่ม
 *   GET/PUT admin/homepage/hero      (ภาพพื้นหลัง)
 *   GET/PUT admin/homepage/banner    (แบนเนอร์โครงการ)
 *   GET/PUT admin/homepage/shortcut  (หมวดหมู่ + ไลฟ์สไตล์ + แนวรถไฟฟ้า)
 *   GET/PUT admin/homepage/feature   (จุดเด่นบริการ + ตัวเลข)
 *   GET/PUT admin/homepage/article   (บทความ)
 *   GET/PUT admin/homepage/contact   (แถบติดต่อ + ฟุตเตอร์ — ยังไม่เปิดเมนู)
 *
 * ไฟล์นี้เหลือเฉพาะ "ตัวเลือกของ UI" (ชุดไอคอน สี ธีม หมวดหมู่บทความ) ซึ่งเป็น
 * ข้อจำกัดด้านดีไซน์ ไม่ใช่ข้อมูล จึงคุมไว้ในโค้ดให้ทั้งระบบใช้ชุดเดียวกัน
 * ========================================================================== */
import ICON from "../page/homepage/icons.js";

/* หัวเอกสารนโยบาย/ข้อกำหนดเปล่า — สองภาษาเสมอ ทั้งคู่มีฟิลด์ชุดเดียวกัน */
const emptyLegalHead = () => ({ title: "", subtitle: "", lead: "", updated: "" });
const emptyLegalDoc = () => ({ th: emptyLegalHead(), en: emptyLegalHead() });

/* โครงเปล่าของแต่ละกลุ่ม — ใช้ตั้งค่า state ตั้งต้นก่อน API ตอบ
   หน้าจอจึง render ได้ทันทีโดยไม่ต้องเช็ก null ทีละจุด */
export const EMPTY_GROUP = {
  hero: { setting: { autoplay: true, interval: 6 }, slides: [] },
  banner: { items: [] },
  post: { setting: { src: "", line_id: "" } },
  shortcut: {
    categories: [],
    lifestyles: { section: { title: "" }, items: [] },
    transit: { section: { title: "" }, items: [] },
  },
  feature: { section: { title: "" }, items: [], stats: [] },
  article: { section: { title: "" }, items: [] },
  contact: {
    bar: { title: "", desc: "", cta: "home_cta_contact", items: [] },
    footer: { tagline: "", newsletter_title: "", newsletter_desc: "", company: "" },
    socials: [],
  },
  privacy: { doc: emptyLegalDoc(), items: [] },
  terms: { doc: emptyLegalDoc(), items: [] },
};

export const clone = (value) => JSON.parse(JSON.stringify(value));

/* ---------------------------------------------------------------- ตัวช่วย */
export const nextSort = (list) => (list.length ? Math.max(...list.map((e) => e.sort || 0)) + 1 : 1);
export const newUid = (prefix) => `${prefix}-${Date.now()}`;
export const ordered = (list) => [...(list || [])].sort((a, b) => (a.sort || 0) - (b.sort || 0));

/* สลับลำดับกับรายการที่อยู่ติดกัน (step = -1 ขึ้น, 1 ลง)
   ต้องย้ายตำแหน่งใน array จริง ไม่ใช่สลับแค่ค่า sort เพราะ API บันทึกลำดับ
   จากตำแหน่งใน array ที่ส่งไป (sort เดิมถูกเขียนทับด้วย index) */
export const moveItem = (list, uid, step) => {
  const rows = ordered(list);
  const index = rows.findIndex((row) => row.uid === uid);
  if (index < 0 || !rows[index + step]) return list;

  rows.splice(index + step, 0, rows.splice(index, 1)[0]);
  return rows.map((row, i) => ({ ...row, sort: i + 1 }));
};

/* ------------------------------------------------------- ภาพจำลอง (SVG) */
/* ใช้เมื่อยังไม่ได้อัปโหลดรูปจริง — ชุดเดียวกับที่ HomeMedia วาดบนหน้าเว็บ */
export const MEDIA_KINDS = [
  { value: "house", label: "home_kind_house" },
  { value: "tower", label: "home_kind_tower" },
  { value: "lowrise", label: "home_kind_lowrise" },
  { value: "town", label: "home_kind_town" },
  { value: "shop", label: "home_kind_shop" },
  { value: "land", label: "home_kind_land" },
  { value: "skyline", label: "home_kind_skyline" },
  { value: "abstract", label: "home_kind_abstract" },
];

/* โทนสีบรรยากาศของภาพจำลอง 8 แบบ (ตรงกับ TONES ใน HomeMedia) */
export const MEDIA_TONES = [0, 1, 2, 3, 4, 5, 6, 7];

/* จุดโฟกัสของรูปพื้นหลัง — คุมว่าจะเห็นส่วนไหนของรูปเมื่อถูกครอบ */
export const HERO_POSITIONS = [
  { value: "center", label: "home_pos_center" },
  { value: "center 30%", label: "home_pos_top" },
  { value: "center 42%", label: "home_pos_middle" },
  { value: "center 65%", label: "home_pos_bottom" },
  { value: "left center", label: "home_pos_left" },
  { value: "right center", label: "home_pos_right" },
];

/* จังหวะเปลี่ยนภาพพื้นหลัง (วินาที) */
export const HERO_INTERVALS = [
  { value: "4", label: "4" },
  { value: "6", label: "6" },
  { value: "8", label: "8" },
  { value: "10", label: "10" },
];

/* ------------------------------------------------------------ แบนเนอร์ */
/* ช่องข้อความทั้งหมดของแบนเนอร์ — ใช้ตรวจว่าใบนี้เป็น "รูปล้วน" หรือไม่
   ไฟล์ภาพแบนเนอร์ส่วนใหญ่มีข้อความอยู่ในรูปอยู่แล้ว ถ้าไม่ได้กรอกช่องไหนเลย
   หน้าเว็บจะไม่เติมตัวอักษรหรือฉากมืดคลุมทับ และย่อ/ขยายตามสัดส่วนของรูป */
export const BANNER_TEXT_FIELDS = ["kicker", "title", "sub", "brand", "brand_sub", "headline", "detail", "price"];

export const hasBannerText = (item) => BANNER_TEXT_FIELDS.some((key) => !!String(item?.[key] || "").trim());

/* ชื่อไว้เรียกแบนเนอร์ในหน้าจอแอดมิน (หัวข้อยืนยันลบ / alt ของรูป)
   ใบรูปล้วนไม่มีข้อความให้ใช้ จึงคืนค่าว่างไว้ ให้ผู้เรียกเลือกคำแทนเอง */
export const bannerLabel = (item) => String(item?.brand || item?.headline || item?.title || "").trim();

/* --------------------------------------------------- สีพื้นหลังแบนเนอร์ */
export const BANNER_THEMES = [
  { value: "linear-gradient(120deg, #0b1533 0%, #142a5c 52%, #1c3f86 100%)", label: "home_theme_navy" },
  { value: "linear-gradient(120deg, #1a1206 0%, #3d2c14 52%, #5c4520 100%)", label: "home_theme_gold" },
  { value: "linear-gradient(120deg, #0b120d 0%, #1d3324 52%, #2b4c33 100%)", label: "home_theme_ink" },
  { value: "linear-gradient(120deg, #2a0f1c 0%, #4d1c31 52%, #74294a 100%)", label: "home_theme_wine" },
  { value: "linear-gradient(120deg, #101828 0%, #24344d 52%, #3b5372 100%)", label: "home_theme_steel" },
];

/* ---------------------------------------------- ไอคอนของการ์ดจุดเด่น */
/* เลือกจากชุดที่เตรียมไว้ เพื่อให้ไอคอนทั้งหน้าเป็นชุดเดียวกัน (fa-light) */
export const FEATURE_ICONS = [
  "", // โล่ป้องกัน
  "", // จับมือ
  "", // เครื่องคิดเลข
  "", // หูฟังบริการ
  "", // บ้าน
  "", // ทีมงาน
  "", // แผนที่
  "", // ธนบัตร
  "", // ถูกต้อง
  "", // ดาว
  "", // เวลา
  "", // รวดเร็ว
];

/* -------------------------------------------- ไอคอนของทางลัดค้นหา */
/* ใช้ร่วมกันทั้งหมวดหมู่ยอดนิยม ไลฟ์สไตล์ และแนวรถไฟฟ้า — คุมให้เป็นชุด
   fa-light ชุดเดียวกับทั้งหน้า ไม่ให้แต่ละแถวหน้าตาหลุดกัน */
export const SHORTCUT_ICONS = [
  ICON.building,
  ICON.home,
  ICON.town,
  ICON.shop,
  ICON.train,
  ICON.subway,
  ICON.plane,
  ICON.paw,
  ICON.graduation,
  ICON.chart_line,
  ICON.gift,
  ICON.briefcase,
  ICON.tree,
  ICON.marker,
  ICON.key,
  ICON.crown,
];

/* สีวงไอคอนของสายรถไฟฟ้า — ตั้งตามสีสายจริงเพื่อให้ผู้ใช้กวาดตาหาสายของตัวเองเจอ */
export const TRANSIT_COLORS = [
  { value: "#0b7a3b", label: "home_color_green" },
  { value: "#1d4ed8", label: "home_color_blue" },
  { value: "#6d28d9", label: "home_color_purple" },
  { value: "#b45309", label: "home_color_yellow" },
  { value: "#be185d", label: "home_color_pink" },
  { value: "#b91c1c", label: "home_color_red" },
  { value: "#334155", label: "home_color_grey" },
];

/* ------------------------------------------------ ช่องทางติดต่อ / โซเชียล */
export const CONTACT_KINDS = [
  { value: "phone", label: "home_contact_kind_phone" },
  { value: "line", label: "home_contact_kind_line" },
  { value: "mail", label: "home_contact_kind_mail" },
  { value: "map", label: "home_contact_kind_map" },
  { value: "clock", label: "home_contact_kind_clock" },
];

/* ฟอนต์ในโปรเจกต์มีเฉพาะชุด light ไม่มีชุด brand จึงเลือกไอคอนทั่วไปที่สื่อ
   ความหมายใกล้เคียงแทนโลโก้แต่ละแพลตฟอร์ม */
export const SOCIAL_KEYS = [
  { value: "facebook", label: "Facebook", icon: ICON.globe },
  { value: "line", label: "LINE", icon: ICON.comment },
  { value: "youtube", label: "YouTube", icon: ICON.play },
  { value: "instagram", label: "Instagram", icon: ICON.camera },
  { value: "tiktok", label: "TikTok", icon: ICON.play },
  { value: "x", label: "X", icon: ICON.globe },
];

/* ------------------------------------------------------- หมวดหมู่บทความ */
export const ARTICLE_TAGS = [
  { value: "คู่มือผู้ซื้อ", label: "คู่มือผู้ซื้อ" },
  { value: "การลงทุน", label: "การลงทุน" },
  { value: "กฎหมายและภาษี", label: "กฎหมายและภาษี" },
  { value: "สินเชื่อ", label: "สินเชื่อ" },
  { value: "แต่งบ้าน", label: "แต่งบ้าน" },
  { value: "ข่าวสารโครงการ", label: "ข่าวสารโครงการ" },
];

/* ------------------------------------------- เอกสารนโยบาย / ข้อกำหนด */
/* สองเมนูนี้ใช้หน้าจอเดียวกัน ต่างกันแค่ปลายทาง API และคำบนหัวข้อ */
export const LEGAL_PAGES = {
  privacy: {
    group: "privacy",
    title: "home_privacy_manage",
    desc: "home_privacy_manage_desc",
    site: "/policy-privacy",
    // ใช้เป็น id ของหัวข้อบนหน้าเว็บจริง (#policy-<key>) ต้องตรงกับหน้าสาธารณะ
    anchor: "policy",
  },
  terms: {
    group: "terms",
    title: "home_terms_manage",
    desc: "home_terms_manage_desc",
    site: "/terms-of-use",
    anchor: "terms",
  },
};

/* ภาษาที่แก้ได้ — เอกสารเก็บสองภาษาในรายการเดียวกัน สลับแท็บแล้วแก้คนละชุด */
export const LEGAL_LANGS = [
  { value: "th", label: "lang_thai" },
  { value: "en", label: "lang_english" },
];

/* ชนิดของบล็อกเนื้อหา — ต้องตรงกับที่ renderer ฝั่งหน้าเว็บรองรับ
   (ดู renderBlock ใน policy_privacy.js / terms_use.js) */
export const LEGAL_BLOCK_TYPES = [
  { value: "p", label: "home_legal_block_p" },
  { value: "ul", label: "home_legal_block_ul" },
  { value: "note", label: "home_legal_block_note" },
  { value: "link", label: "home_legal_block_link" },
];

/* คำแทนที่ข้อมูลบริษัท — พิมพ์ลงในเนื้อหาได้เลย หน้าเว็บจะแทนค่าจริงจาก
   "ตั้งค่า › ข้อมูลบริษัท" ตอนแสดงผล จะได้ไม่ต้องไล่แก้เอกสารเมื่อบริษัทเปลี่ยนเบอร์
   บรรทัดที่แทนแล้วได้ค่าว่าง (เช่น {hotline} ที่ยังไม่ได้กรอก) จะถูกซ่อนไปเอง */
export const LEGAL_TOKENS = [
  "{company_name}",
  "{brand}",
  "{website}",
  "{email}",
  "{phone}",
  "{address}",
  "{hotline}",
  "{line_oa}",
];

/* บล็อกเปล่าของแต่ละชนิด — เก็บทุกฟิลด์ไว้ครบ สลับชนิดไปมาแล้วข้อความที่พิมพ์ไว้
   จะได้ไม่หาย (ฝั่ง backend ตัดฟิลด์ที่ไม่เกี่ยวกับชนิดนั้นทิ้งตอนบันทึกอยู่แล้ว) */
export const newBlock = (type = "p") => ({
  _id: newUid("bk"),
  type,
  text: "",
  href: "",
  items: type === "ul" ? [{ _id: newUid("li"), label: "", text: "" }] : [],
});

/* ติด _id ให้ทุกบล็อก/รายการย่อยตอนเปิดฟอร์ม — ใช้เป็น key ของ React เท่านั้น
   ถ้าใช้ index เป็น key ช่องที่พิมพ์อยู่จะสลับค่ากันเองเวลาลบหรือสลับลำดับ */
export const withBlockIds = (blocks = []) =>
  blocks.map((block) => ({
    _id: newUid("bk"),
    type: block.type || "p",
    text: block.text || "",
    href: block.href || "",
    items: (block.items || []).map((item) =>
      typeof item === "string"
        ? { _id: newUid("li"), label: "", text: item }
        : { _id: newUid("li"), label: item.label || "", text: item.text || "" }
    ),
  }));

/** ถอด _id ออกก่อนส่งขึ้น API — เก็บเฉพาะฟิลด์ที่ชนิดนั้นใช้จริง */
export const stripBlockIds = (blocks = []) =>
  blocks.map((block) => {
    if (block.type === "ul") {
      return {
        type: "ul",
        items: (block.items || [])
          .filter((item) => (item.label || "").trim() || (item.text || "").trim())
          .map((item) => ({ label: (item.label || "").trim(), text: (item.text || "").trim() })),
      };
    }
    if (block.type === "link") return { type: "link", href: (block.href || "").trim(), text: (block.text || "").trim() };
    return { type: block.type === "note" ? "note" : "p", text: (block.text || "").trim() };
  });

/** ข้อความย่อของบล็อกสำหรับโชว์ในรายการหัวข้อ (ไม่ต้องเปิดฟอร์มก็พอเห็นเนื้อหา) */
export const blockSummary = (block) => {
  if (!block) return "";
  if (block.type === "ul") return (block.items || []).map((item) => item.label || item.text).filter(Boolean).join(" · ");
  return block.text || block.href || "";
};

/* ---------------------------------------------------------- ไอคอนหน้าแอดมิน */
/* เก็บเป็น \u escape เพื่อให้อ่านออกในโค้ด (อักขระ FontAwesome มองไม่เห็น) */
export const ADMIN_ICON = {
  plus: "\uf067",
  save: "\uf0c7",
  reset: "\uf0e2",
  edit: "\uf044",
  trash: "\uf2ed",
  up: "\uf062",
  down: "\uf063",
  image: "\uf03e",
  eye: "\uf06e",
  external: "\uf35d",
  info: "\uf05a",
  check: "\uf058",
  clock: "\uf017",
  tag: "\uf02b",
  text: "\uf031",
  palette: "\uf53f",
  star: "\uf005",
  play: "\uf04b",
  pause: "\uf04c",
  grid: "\uf00a",
  list: "\uf03a",
  chart: "\uf080",
  news: "\uf1ea",
  photo: "\uf302",
  link: "\uf0c1",
  quote: "\uf10d",
  doc: "\uf15c",
  language: "\uf1ab",
  chevron_left: "\uf053",
  chevron_right: "\uf054",
};
